import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiMail, FiLock, FiEye, FiEyeOff, FiShield, FiUser, FiCheck, FiKey } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import PasskeyRegistration from '../components/Passkey/PasskeyRegistration';
import toast from 'react-hot-toast';
import api from '../services/api';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  username: yup.string()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be less than 30 characters')
    .matches(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens')
    .optional(),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  acceptTerms: yup.boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required()
});

type FormData = {
  email: string;
  username?: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser, isGuest, convertGuestToUser, refreshUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usePasskeyOnly, setUsePasskeyOnly] = useState(false);
  const [passkeyEmail, setPasskeyEmail] = useState('');
  const [passkeyRegistered, setPasskeyRegistered] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any
  });

  const password = watch('password', '');

  const getPasswordStrength = () => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/)) strength++;
    if (password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await registerUser(data.email, data.password, data.username);
      
      // Convert guest session if applicable
      if (isGuest) {
        await convertGuestToUser();
      }
      
      navigate('/dashboard');
    } catch (error) {
      // Error is already handled in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyRegistrationSuccess = async () => {
    setPasskeyRegistered(true);
    toast.success('Account created with passkey!');
    
    // Refresh user data to update auth context
    await refreshUser();
    
    // After successful passkey registration, navigate to dashboard
    navigate('/dashboard');
  };

  const handlePasskeyOnlySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passkeyEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    // Register the user using passwordless endpoint
    setIsLoading(true);
    try {
      const response = await api.post('/auth/register/passwordless', {
        email: passkeyEmail
      });
      
      // Store the token
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }
      
      // Refresh user data to update auth context
      await refreshUser();
      
      // Show passkey registration component
      setPasskeyRegistered(true);
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('An account with this email already exists');
      } else {
        toast.error('Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center space-x-2">
          <FiShield className="w-10 h-10 text-primary-600" />
          <span className="text-2xl font-bold text-gray-900">AnonLink</span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Guest conversion notice */}
          {isGuest && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex">
                <FiCheck className="h-5 w-5 text-green-400 mt-0.5" />
                <div className="ml-3">
                  <p className="text-sm text-green-800">
                    Your guest links will be saved to your new account!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Registration method toggle */}
          <div className="mb-6">
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setUsePasskeyOnly(false)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  !usePasskeyOnly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Email & Password
              </button>
              <button
                type="button"
                onClick={() => setUsePasskeyOnly(true)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  usePasskeyOnly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiKey className="inline-block w-4 h-4 mr-1" />
                Passkey Only
              </button>
            </div>
          </div>

          {usePasskeyOnly ? (
            // Passkey-only registration
            !passkeyRegistered ? (
              <form onSubmit={handlePasskeyOnlySubmit} className="space-y-6">
                <div>
                  <label htmlFor="passkeyEmail" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type="email"
                      value={passkeyEmail}
                      onChange={(e) => setPasskeyEmail(e.target.value)}
                      className="input pl-10"
                      placeholder="you@example.com"
                      required
                    />
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    Passwordless Account
                  </p>
                  <p className="text-sm text-blue-700">
                    You'll use a passkey (Face ID, Touch ID, Windows Hello, or security key) to sign in. No password needed!
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary w-full flex justify-center"
                >
                  {isLoading ? 'Creating account...' : 'Continue with Passkey'}
                </button>
              </form>
            ) : (
              <PasskeyRegistration
                userEmail={passkeyEmail}
                onSuccess={handlePasskeyRegistrationSuccess}
                isNewUser={true}
              />
            )
          ) : (
            // Traditional email/password registration
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="input pl-10"
                  placeholder="you@example.com"
                />
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Username (optional) */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username <span className="text-gray-500">(optional)</span>
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className="input pl-10"
                  placeholder="johndoe"
                />
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600">Password strength</span>
                    <span className={`font-medium ${
                      passwordStrength <= 2 ? 'text-red-600' :
                      passwordStrength === 3 ? 'text-yellow-600' :
                      passwordStrength === 4 ? 'text-green-600' :
                      'text-green-700'
                    }`}>
                      {passwordStrength <= 2 ? 'Weak' :
                       passwordStrength === 3 ? 'Fair' :
                       passwordStrength === 4 ? 'Good' :
                       'Strong'}
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength
                            ? passwordStrength <= 2 ? 'bg-red-500' :
                              passwordStrength === 3 ? 'bg-yellow-500' :
                              passwordStrength === 4 ? 'bg-green-500' :
                              'bg-green-600'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Terms and conditions */}
            <div className="flex items-start">
              <input
                {...register('acceptTerms')}
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
              />
              <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                  Privacy Policy
                </Link>
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-red-600">{errors.acceptTerms.message}</p>
            )}

            {/* Submit button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full flex justify-center"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
          )}

          {/* Benefits */}
          <div className="mt-6 space-y-2">
            <p className="text-sm font-medium text-gray-700">With a free account you get:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center">
                <FiCheck className="w-4 h-4 text-green-500 mr-2" />
                10 shortened links per month
              </li>
              <li className="flex items-center">
                <FiCheck className="w-4 h-4 text-green-500 mr-2" />
                Link analytics and click tracking
              </li>
              <li className="flex items-center">
                <FiCheck className="w-4 h-4 text-green-500 mr-2" />
                Custom aliases for your links
              </li>
              <li className="flex items-center">
                <FiCheck className="w-4 h-4 text-green-500 mr-2" />
                QR code generation
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;